import React, { useState, useEffect } from 'react';
import styles from './FileUploadInput.module.scss';
import Garbage from '@src/assets/Garbage.png';
import Plus from '@src/assets/Plus.png';
import Loader from '../Loader';
import Edit from '@src/assets/Edit.png';
import Tooltip from '@mui/material/Tooltip';

type FileUploadInputProps = {
    title: string;
    documentName?: string;
    isValidated?: boolean;
    initialFileName?: string;
    initialFileSize?: number;
    selectFile: (file: File) => void;
    deleteFile?: () => void;
    accept?: string;
    isApproved?: boolean;
    isLoading?: boolean;
    isCompanyFile?: boolean;
    hasError?: boolean;
    errorMessage?: string
}

const FileUploadInput: React.FC<FileUploadInputProps> = ({
    title,
    documentName,
    isValidated = false,
    initialFileName = '',
    initialFileSize = 0,
    selectFile,
    deleteFile,
    accept,
    isApproved,
    isLoading,
    isCompanyFile = false,
    hasError,
    errorMessage
}) => {
    const [fileName, setFileName] = useState<string>(initialFileName);
    const [fileSize, setFileSize] = useState<number>(initialFileSize);

    useEffect(() => {
        setFileSize(initialFileSize);
        setFileName(initialFileName);
    }, [initialFileSize, initialFileName]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            selectFile(file)
            setFileName(file.name);
            setFileSize(file.size);
        }
    };

    return (
        <Tooltip title={['Liasse fiscale', 'Liasse fiscale N-1'].includes(title) ?
            <div>
                <p className={styles.tooltipText}>
                    Pour faciliter la reprise de votre dossier, merci de nommer vos fichiers comme suit :
                </p>
                <ul className={styles.tooltipList}>
                    <li className={styles.tooltipText}>
                        <strong>liasse-N.pdf</strong> pour la dernière liasse fiscale (exercice le plus récent)
                    </li>
                    <li className={styles.tooltipText}>
                        <strong>liasse-N-1.pdf</strong> pour l'avant-dernière liasse fiscale (exercice précédent)
                    </li>
                </ul>
            </div>
            : null}>
            <div className={`${styles.uploadContainer} ${isValidated ? styles.validated : ''} ${hasError ? styles.error : ''}`} data-error={errorMessage}>
                <div className={styles.uploadIcon}>
                    <svg viewBox="0 0 23 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M12.1389 0C12.4917 0 12.7778 0.284913 12.7778 0.636364V6.36364C12.7778 8.47235 14.494 10.1818 16.6111 10.1818H22.3611C22.7139 10.1818 23 10.4667 23 10.8182V24.1818C23 26.2906 21.2838 28 19.1667 28H3.83333C1.71625 28 0 26.2906 0 24.1818V3.81818C0 1.70946 1.71625 0 3.83333 0H12.1389ZM12.4035 13.1001C11.9045 12.6031 11.0955 12.6031 10.5965 13.1001L6.76314 16.9182C6.26414 17.4152 6.26414 18.2211 6.76314 18.7181C7.26215 19.2151 8.07119 19.2151 8.5702 18.7181L10.2222 17.0726V21.6364C10.2222 22.3393 10.7943 22.9091 11.5 22.9091C12.2057 22.9091 12.7778 22.3393 12.7778 21.6364V17.0726L14.4298 18.7181C14.9288 19.2151 15.7379 19.2151 16.2369 18.7181C16.7358 18.2211 16.7358 17.4152 16.2369 16.9182L12.4035 13.1001ZM15.3333 0.218362C15.3333 0.0977708 15.4315 0 15.5526 0C16.2303 0 16.8803 0.268176 17.3596 0.745551L22.2515 5.61809C22.7308 6.09546 23 6.7429 23 7.418C23 7.53859 22.9019 7.63636 22.7807 7.63636H16.6111C15.9054 7.63636 15.3333 7.06654 15.3333 6.36364V0.218362Z" fill="var(--color-a)"/>
                    </svg>
                </div>
                {isApproved ?
                    <div className={styles.uploadText}>
                        <strong>{fileName || title}</strong>
                        <span>Ce document est déjà validé pour cette entreprise.</span>
                    </div>
                    :
                    <>
                        <div className={styles.uploadText}>
                            <strong>{title}</strong>
                            <span>
                                {isValidated
                                    ? `${documentName} - ${(fileSize / (1024 * 1024)).toFixed(2)} Mo ${isApproved ? '- En cours de traitement' : ''}`
                                    : 'Glisser - déposer  maximum 10 Mo'}
                            </span>
                        </div>
                        {isLoading ? <Loader type="BUBBLES" yellowBubbles />
                            :
                            isValidated ?
                                <>
                                    {isCompanyFile ?
                                        <>
                                            <input
                                                type="file"
                                                accept={accept}
                                                className={styles.fileInput}
                                                onChange={handleFileChange}
                                            />
                                            <button type="button" className={styles.deleteButton}>
                                                <img src={Edit} alt="Edit" width={22} height={22} />
                                            </button>
                                        </>
                                        :
                                        <button type="button" className={styles.deleteButton} onClick={deleteFile}>
                                            <img src={Garbage} alt="Garbage" width={21} height={21} />
                                        </button>
                                    }
                                </>
                                :
                                <>
                                    <button type="button" className={styles.uploadButton}>
                                        <img src={Plus} alt="Plus" width={30} height={30} />
                                    </button>
                                    <input
                                        type="file"
                                        accept={accept}
                                        className={styles.fileInput}
                                        onChange={handleFileChange}
                                        disabled={isValidated}
                                    />
                                </>
                        }
                    </>
                }
            </div>
        </Tooltip>
    );
};

export default FileUploadInput;
